/**
 * TriageClassifier - Clasificación START/SALT para triage médico
 * Implementa algoritmos de triage de emergencia estandarizados
 */

import {
  TriagePriority,
  ClinicalNodeData,
  PatientGraph,
} from '../types/medical';
import {
  calculateNEWS2,
  calculateqSOFA,
  ScaleScore,
} from '../types/clinical';

// ═══════════════════════════════════════════════════════════════════════════════
// ALGORITMO START (Simple Triage and Rapid Treatment)
// ═══════════════════════════════════════════════════════════════════════════════

export interface STARTCriteria {
  canWalk: boolean;
  respiratoryRate: number;
  perfusion: 'radial' | 'carotid' | 'none';
  mentalStatus: 'alert' | 'verbal' | 'pain' | 'unresponsive';
  respiratoryEffort: 'normal' | 'labored' | 'absent';
}

export class STARTClassifier {
  
  classify(criteria: STARTCriteria): TriagePriority {
    // 1. Los que pueden caminar = MINOR
    if (criteria.canWalk) {
      return 'minor';
    }

    // 2. Evaluar respiración
    if (criteria.respiratoryRate === 0) {
      // Posicionar y abrir vía aérea
      // Si aún no respira = EXPECTANT/DECEASED
      return 'expectant';
    }

    if (criteria.respiratoryRate > 30) {
      return 'immediate';
    }

    // 3. Evaluar perfusión
    if (criteria.perfusion === 'none') {
      return 'immediate';
    }

    if (criteria.perfusion === 'carotid') {
      return 'immediate';
    }

    // 4. Evaluar estado mental
    if (criteria.mentalStatus !== 'alert') {
      return 'immediate';
    }

    // 5. Si llega aquí = DELAYED
    return 'delayed';
  }

  classifyFromNodes(nodes: ClinicalNodeData[]): TriagePriority {
    // Extraer datos de los nodos
    const rrNode = nodes.find(n => n.concept.code === '9279-1');
    const hrNode = nodes.find(n => n.concept.code === '8867-4');
    const gcsNode = nodes.find(n => n.concept.code === '9269-2');

    const criteria: Partial<STARTCriteria> = {};

    // Respiración
    if (rrNode?.valueQuantity) {
      criteria.respiratoryRate = rrNode.valueQuantity.value;
    }

    // Perfusión (basada en pulso)
    if (hrNode?.valueQuantity) {
      const hr = hrNode.valueQuantity.value;
      if (hr === 0) criteria.perfusion = 'none';
      else if (hr < 50) criteria.perfusion = 'carotid';
      else criteria.perfusion = 'radial';
    }

    // Estado mental
    if (gcsNode?.valueQuantity) {
      const gcs = gcsNode.valueQuantity.value;
      if (gcs >= 14) criteria.mentalStatus = 'alert';
      else if (gcs >= 10) criteria.mentalStatus = 'verbal';
      else if (gcs >= 6) criteria.mentalStatus = 'pain';
      else criteria.mentalStatus = 'unresponsive';
    }

    return this.classify(criteria as STARTCriteria);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// ALGORITMO SALT (Sort, Assess, Lifesaving Interventions, Treatment/Transport)
// ═══════════════════════════════════════════════════════════════════════════════

export interface SALTCriteria {
  lifeThreatening: boolean;
  canFollowCommands: boolean;
  canWalk: boolean;
  respiratoryRate: number;
  spo2: number;
  hemorrhageControl: boolean;
}

export class SALTClassifier {
  
  classify(criteria: SALTCriteria): TriagePriority {
    // SALT es más detallado que START
    
    // 1. Lesión que amenaza vida inmediatamente
    if (criteria.lifeThreatening) {
      return 'immediate';
    }

    // 2. Puede caminar
    if (criteria.canWalk) {
      return 'minor';
    }

    // 3. No puede seguir comandos
    if (!criteria.canFollowCommands) {
      return 'immediate';
    }

    // 4. Problemas respiratorios
    if (criteria.respiratoryRate < 10 || criteria.respiratoryRate > 30) {
      return 'immediate';
    }

    if (criteria.spo2 < 90) {
      return 'immediate';
    }

    // 5. Sangrado no controlado
    if (!criteria.hemorrhageControl) {
      return 'immediate';
    }

    // 6. Todo lo demás que puede esperar
    return 'delayed';
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// CLASIFICADOR INTEGRADO
// ═══════════════════════════════════════════════════════════════════════════════

export interface TriageAssessment {
  priority: TriagePriority;
  confidence: number;
  method: 'START' | 'SALT' | 'NEWS2' | 'qSOFA' | 'manual';
  reasons: string[];
  scaleScore?: ScaleScore;
}

export class TriageClassifier {
  private startClassifier = new STARTClassifier();
  private saltClassifier = new SALTClassifier();

  classify(
    patient: PatientGraph,
    method: 'auto' | 'START' | 'SALT' | 'NEWS2' | 'qSOFA' = 'auto'
  ): TriageAssessment {
    const nodes = patient.nodes;

    // Determinar método
    let actualMethod = method;
    if (method === 'auto') {
      // Usar NEWS2 si hay suficientes datos
      if (this.hasNEWS2Data(nodes)) {
        actualMethod = 'NEWS2';
      } else {
        actualMethod = 'START';
      }
    }

    // Clasificar según método
    switch (actualMethod) {
      case 'START':
        return this.classifySTART(nodes);
      
      case 'SALT':
        return this.classifySALT(nodes);
      
      case 'NEWS2':
        return this.classifyNEWS2(nodes, patient.demographics.age);
      
      case 'qSOFA':
        return this.classifyqSOFA(nodes);
      
      default:
        return this.classifySTART(nodes);
    }
  }

  private classifySTART(nodes: ClinicalNodeData[]): TriageAssessment {
    const priority = this.startClassifier.classifyFromNodes(nodes);
    const reasons: string[] = [];

    // Explicar razones
    const rrNode = nodes.find(n => n.concept.code === '9279-1');
    if (rrNode?.valueQuantity && rrNode.valueQuantity.value > 30) {
      reasons.push(`Frecuencia respiratoria elevada (${rrNode.valueQuantity.value})`);
    }

    const hrNode = nodes.find(n => n.concept.code === '8867-4');
    if (hrNode?.valueQuantity && hrNode.valueQuantity.value > 120) {
      reasons.push(`Taquicardia severa (${hrNode.valueQuantity.value})`);
    }

    return {
      priority,
      confidence: 0.85,
      method: 'START',
      reasons,
    };
  }

  private classifySALT(nodes: ClinicalNodeData[]): TriageAssessment {
    // Implementación similar a START pero con criterios SALT
    const priority = this.startClassifier.classifyFromNodes(nodes);
    
    return {
      priority,
      confidence: 0.8,
      method: 'SALT',
      reasons: ['Evaluación SALT completa'],
    };
  }

  private classifyNEWS2(nodes: ClinicalNodeData[], _age: number): TriageAssessment {
    const news2Data = this.extractNEWS2Data(nodes);
    
    if (!news2Data.complete) {
      return this.classifySTART(nodes);
    }

    const score = calculateNEWS2(
      news2Data.respiratoryRate!,
      news2Data.spo2!,
      news2Data.systolicBP!,
      news2Data.pulse!,
      news2Data.consciousness!,
      news2Data.temperature!
    );

    let priority: TriagePriority;
    if (score.total >= 7) {
      priority = 'immediate';
    } else if (score.total >= 5) {
      priority = 'immediate';
    } else if (score.total >= 3) {
      priority = 'delayed';
    } else {
      priority = 'minor';
    }

    return {
      priority,
      confidence: 0.9,
      method: 'NEWS2',
      reasons: [`NEWS2 Score: ${score.total} - ${score.interpretation}`],
      scaleScore: score,
    };
  }

  private classifyqSOFA(nodes: ClinicalNodeData[]): TriageAssessment {
    const qsofaData = this.extractqSOFAData(nodes);
    
    if (!qsofaData.complete) {
      return this.classifySTART(nodes);
    }

    const score = calculateqSOFA(
      qsofaData.respiratoryRate!,
      qsofaData.systolicBP!,
      qsofaData.alteredMentalStatus!
    );

    const priority: TriagePriority = score.total >= 2 ? 'immediate' : 'delayed';

    return {
      priority,
      confidence: 0.85,
      method: 'qSOFA',
      reasons: [`qSOFA Score: ${score.total} - ${score.interpretation}`],
      scaleScore: score,
    };
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // EXTRACCIÓN DE DATOS
  // ═════════════════════════════════════════════════════════════════════════════

  private hasNEWS2Data(nodes: ClinicalNodeData[]): boolean {
    const required = ['9279-1', '2708-6', '8480-6', '8867-4', '8310-5'];
    const found = required.filter(code => 
      nodes.some(n => n.concept.code === code && n.valueQuantity !== undefined)
    );
    return found.length >= 4;
  }

  private extractNEWS2Data(nodes: ClinicalNodeData[]) {
    const rr = nodes.find(n => n.concept.code === '9279-1');
    const spo2 = nodes.find(n => n.concept.code === '2708-6');
    const bp = nodes.find(n => n.concept.code === '8480-6');
    const hr = nodes.find(n => n.concept.code === '8867-4');
    const temp = nodes.find(n => n.concept.code === '8310-5');
    const gcs = nodes.find(n => n.concept.code === '9269-2');

    const consciousness: 'alert' | 'verbal' | 'pain' | 'unresponsive' = gcs?.valueQuantity?.value 
      ? (gcs.valueQuantity.value >= 14 ? 'alert' : gcs.valueQuantity.value >= 9 ? 'verbal' : gcs.valueQuantity.value >= 5 ? 'pain' : 'unresponsive')
      : 'alert';

    return {
      respiratoryRate: rr?.valueQuantity?.value,
      spo2: spo2?.valueQuantity?.value,
      systolicBP: bp?.valueQuantity?.value,
      pulse: hr?.valueQuantity?.value,
      temperature: temp?.valueQuantity?.value,
      consciousness,
      complete: !!(rr?.valueQuantity && spo2?.valueQuantity && bp?.valueQuantity && hr?.valueQuantity),
    };
  }

  private extractqSOFAData(nodes: ClinicalNodeData[]) {
    const rr = nodes.find(n => n.concept.code === '9279-1');
    const bp = nodes.find(n => n.concept.code === '8480-6');
    const confusion = nodes.find(n => 
      n.concept.code === '40917007' || 
      (n.type === 'symptom' && n.concept.display?.toLowerCase().includes('confusión'))
    );

    return {
      respiratoryRate: rr?.valueQuantity?.value,
      systolicBP: bp?.valueQuantity?.value,
      alteredMentalStatus: !!confusion,
      complete: !!(rr?.valueQuantity && bp?.valueQuantity),
    };
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // UTILIDADES
  // ═════════════════════════════════════════════════════════════════════════════

  getTriageColor(priority: TriagePriority): string {
    const colors: Record<TriagePriority, string> = {
      immediate: '#DC2626',
      delayed: '#F59E0B',
      minor: '#10B981',
      expectant: '#6B7280',
      deceased: '#111827',
    };
    return colors[priority];
  }

  getTriageLabel(priority: TriagePriority): string {
    const labels: Record<TriagePriority, string> = {
      immediate: 'ATENCIÓN INMEDIATA',
      delayed: 'PUEDE ESPERAR',
      minor: 'AMBULATORIO',
      expectant: 'EXPECTANTE',
      deceased: 'FALLECIDO',
    };
    return labels[priority];
  }
}

export default TriageClassifier;
